import React, { useEffect, useRef, useState } from 'react';
import { Button, Flex, Space, theme, Typography } from 'antd';
import {
	ComponentTypes,
	createDrawerBaseClassName,
	Field,
	FieldColors,
	getDrawerBodyElementSelector,
	IRecipient,
} from '../../../../common/componentUtils';
import { Role } from '../../../../data/interfaces/DocumentTemplate';
import { t } from '../../../../utils';
import { DocumentMetaDataItem } from '../../../Start/DocumentMetaData/DocumentMetaData';
import Roles from '../../../Start/Roles';
import { DropdownSection } from '../DropdownSection';
import { FieldsSection } from '../FieldsSection';
import { LeftPanelModal } from '../LeftPanelModal';

const { Text, Title, Paragraph } = Typography;

interface Props {
	roles: IRecipient[] | undefined;
	fields: Field[] | undefined;
	selectedRole: IRecipient | null;
	selectedField: ComponentTypes | null;
	onSelectRole: (value: string) => void;
	onSelectField: (type: ComponentTypes | null) => void;
	onSaveRole: (role: Role) => void;
}

export const SignerFields = ({
	roles,
	fields,
	selectedRole,
	selectedField,
	onSelectRole,
	onSelectField,
	onSaveRole,
}: Props) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const showModal = () => {
		setIsModalOpen(true);
	};

	const hideModal = () => {
		setIsModalOpen(false);
	};
	const { token } = theme.useToken();
	const getDropdownOptions = () => {
		const options: any[] = [];
		roles?.forEach(role => {
			const fieldColorValues = Object.values(FieldColors);
			const optionColor = fieldColorValues[role.sequence % (fieldColorValues.length - 1)];

			options.push({
				label: role.name,
				value: role.id,
				optionColor,
			});
		});
		return options;
	};

	const handleSaveRole = (role: Role) => {
		onSaveRole(role);
		hideModal();
	};

	const [height, setHeight] = useState('100%');
	const fieldsContainerRef = useRef<HTMLDivElement>(null);
	const fieldsContainerHeightRef = useRef<number>(0); // Save fieldsContainerRef height when fields are available. This is used when window is re-sized with other tab being active otherwise fieldsContainerRef.clientHeight is 0 when Signer fields tab is not active
	const leftPanelSignerFieldsContainerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const fieldsContainerElement = fieldsContainerRef.current;
		if (fieldsContainerElement) {
			fieldsContainerHeightRef.current = fieldsContainerElement.clientHeight;
		}
	}, [fields.length]);

	useEffect(() => {
		const leftPanelSignerFieldsContainerElement =
			leftPanelSignerFieldsContainerRef.current;
		const PADDING = 32;
		const drawerBodyElement = document.querySelector(
			getDrawerBodyElementSelector(createDrawerBaseClassName)
		);
		drawerBodyElement.scrollTop = 0; // reset the scroll position

		const thresholdHeight = 200;

		const resizeObserver = new ResizeObserver((entry: ResizeObserverEntry[]) => {
			const drawerBodyElementHeight = entry[0].contentRect.height;
			if (fields.length === 0) {
				leftPanelSignerFieldsContainerElement.style.height =
					drawerBodyElementHeight - PADDING + 'px';
				leftPanelSignerFieldsContainerElement.style.overflow = 'auto';
			} else {
				const refHeight = fieldsContainerHeightRef.current;
				const newHeight = Math.abs(drawerBodyElementHeight - refHeight) - PADDING;

				if (newHeight <= thresholdHeight) {
					setHeight('100%');
					leftPanelSignerFieldsContainerElement.style.height =
						drawerBodyElementHeight - PADDING + 'px';
					leftPanelSignerFieldsContainerElement.style.overflow = 'auto';
				} else {
					setHeight(newHeight - PADDING / 2 + 'px');
					leftPanelSignerFieldsContainerElement.style.height = 'auto';
					leftPanelSignerFieldsContainerElement.style.overflow = 'hidden';
				}
			}
		});
		if (leftPanelSignerFieldsContainerRef) {
			resizeObserver.observe(drawerBodyElement);
		}
		return () => {
			if (leftPanelSignerFieldsContainerRef) {
				resizeObserver.unobserve(drawerBodyElement);
			}
		};
	}, [fields.length]);

	return (
		<Flex
			vertical
			ref={leftPanelSignerFieldsContainerRef}
			className="leftPanelSignerFieldsContainerElement"
		>
			{roles.length === 0 ? (
				<Flex vertical gap={token.sizeXS}>
					<Title style={{ margin: 0 }} level={5}>
						{t('leftNav.signerFilled.title')}
					</Title>
					<Flex vertical style={{ gap: token.margin }}>
						<Paragraph
							style={{ fontSize: token.fontSizeSM, whiteSpace: 'pre-line', margin: 0 }}
						>
							{t('leftNav.signerFilled.descriptionNoRoles')}
						</Paragraph>
						<Button
							type="primary"
							size="small"
							ghost
							style={{ width: '100%' }}
							onClick={() => {
								showModal();
							}}
						>
							{t('leftNav.signerFilled.addRoles')}
						</Button>
					</Flex>
				</Flex>
			) : (
				<Flex
					vertical
					gap={token.size}
					ref={fieldsContainerRef}
					style={{
						width: '100%',
					}}
				>
					<Space direction="vertical">
						<Title style={{ margin: 0 }} level={5}>
							{t('leftNav.signerFilled.title')}
						</Title>
						<Text style={{ fontSize: token.fontSizeSM }}>
							{t('leftNav.signerFilled.descriptionWithRoles')}
						</Text>
					</Space>
					<DropdownSection
						title={t('leftNav.signerFilled.role')}
						description={t('leftNav.signerFilled.dropdownDescription', {
							roleName: selectedRole?.name,
						})}
						onChange={onSelectRole}
						options={getDropdownOptions()}
						value={selectedRole?.id}
					/>
					<Button
						type="primary"
						size="small"
						ghost
						style={{
							width: '100%',
						}}
						onClick={showModal}
						aria-label={t('leftNav.signerFilled.manageRoles')}
					>
						{t('leftNav.signerFilled.manageRoles')}
					</Button>
					<Space
						direction="vertical"
						style={{
							overflow: 'auto',
							width: '100%',
							height,
						}}
					>
						<FieldsSection>
							<FieldsSection.Fields
								fields={fields}
								selectedField={selectedField}
								onClick={onSelectField}
							/>
						</FieldsSection>
					</Space>
				</Flex>
			)}
			<LeftPanelModal
				title={t('startPage.roleLabel')}
				isOpen={isModalOpen}
				onHideModal={hideModal}
			>
				<DocumentMetaDataItem description={t('startPage.roleDescription')}>
					<Roles isShownInModal onSave={handleSaveRole} />
				</DocumentMetaDataItem>
			</LeftPanelModal>
		</Flex>
	);
};
